import { playSound } from '@/lib/sounds';
import { callPopupStore } from '@/features/calls/popups/callPopupStore';
import { isDesktopShell } from '@/features/desktop/bridge';
import { shellDrawsBanners } from '@/features/desktop/shellSettings';
import { presenceStore } from '@/features/presence/presenceStore';
import type { DeliveryVerdict } from './notificationsAdapter';
import { decideDelivery } from './notificationPolicy';
import { notificationActions } from './notificationStore';
import { popupActions } from './popupStore';
import { settingsStore } from './settingsStore';
import { showDesktopNotification } from './desktopNotifications';
import { notify } from './toastApi';
import type { NotificationItem } from './types';

/** Kinds that surface as the white message popup rather than a generic toast. */
const POPUP_KINDS = new Set(['message', 'mention', 'reply']);

/**
 * Kinds that ALREADY have a dedicated surface, and must not also be toasted.
 *
 * An incoming call raises the full-height dark popup from features/calls with
 * Accept and Decline on it. It was ALSO falling through to `notify.activity`
 * here, so every ring produced two things at once — and because the toast is
 * keyed by notification id rather than by call, a few rings in a row stacked
 * into a column of near-identical cards on top of the very popup you were
 * supposed to be answering. Missed and re-tried calls made it worse.
 *
 * The popup is the better surface (it can be answered), so it wins and the
 * toast is dropped. The notification row and the badge are unaffected — this
 * suppresses one transient banner, not the record of the call.
 */
const HANDLED_ELSEWHERE = new Set(['call_incoming', 'call_missed', 'callIncoming', 'callMissed']);

/** The message sound - a short rising fifth on a mallet (lib/sounds.ts). */
function chime(): void {
  playSound('message');
}

export interface IngestOptions {
  /** Navigate to the notification's deep link. */
  onActivate: (item: NotificationItem) => void;
}

/**
 * The single funnel every notification passes through: store it, then apply the
 * interruption verdict. Runs entirely outside React so a burst of traffic costs
 * no renders beyond the components actually subscribed.
 *
 * THE SERVER DECIDES. `delivery` is computed once in
 * `services/notifyDelivery.ts` against the recipient's own settings, their
 * quiet-hours TIMEZONE and their live presence, and written to
 * `notification_deliveries` so "why didn't I get that" has an answer. Deciding
 * it here was never a policy: a second signed-in session popped the banner this
 * one suppressed, and a tab whose settings had not hydrated suppressed nothing.
 *
 * The local `decideDelivery` remains ONLY as the fallback for a verdict-less
 * event — a REST-hydrated row, the mock adapter, or an API older than 055.
 */
export function ingestNotification(
  item: NotificationItem,
  options: IngestOptions,
  delivery: DeliveryVerdict | null = null,
): void {
  const stored = notificationActions.ingest(item);
  const settings = settingsStore.get();
  const presenceState = presenceStore.get();
  const presence = presenceState.self.effectiveState;
  const decision =
    delivery ??
    decideDelivery(stored, settings, presence, Date.now(), presenceState.automation);

  if (!decision.banner) return;

  /*
   * ── ONE POPUP PER EVENT, AND ON THE DESKTOP IT IS THE SYSTEM ONE ────────
   *
   * The reported defect: "messages nd calls coming seperately in popups rather
   * than system liked popup how microsift teams comes … rather its showing
   * inside teams app". Both halves came from this branch running BOTH arms.
   *
   * A message used to push the white in-app card here AND, separately, raise a
   * native toast — two surfaces for one message whenever the window was
   * unfocused but visible, and only the in-app one when it was focused. Now the
   * shell takes it: exactly one popup, drawn by Windows, over whatever the user
   * is actually working in.
   *
   * THE IN-APP SURFACES ARE NOT DELETED, they are yielded. `decision.desktop`
   * is false when the user has turned desktop banners off in settings, or the
   * server suppressed that channel — and then the in-app card is the correct
   * surface and still appears, on the desktop as well. A browser has no shell,
   * so nothing below this line changes for it at all.
   */
  /* ...AND ONLY WHILE THE SHELL WILL ACTUALLY DRAW IT. With "Native
     notifications" switched off the shell drops whatever it is handed, so the
     banner must stay here, on the in-app card that toggle's own text promises:
     turning it off used to silence every banner on the desktop. */
  const shellOwnsTheBanner = isDesktopShell() && decision.desktop && shellDrawsBanners();

  if (shellOwnsTheBanner) {
    /* A RINGING CALL OWNS THE CORNER — the rule MessagePopupStack applied when
       it was the surface, kept now that it is not. The ringer is an
       always-on-top window at `screen-saver` level and a Windows toast lands
       UNDER it, so a message raised during a ring would be invisible and spent.
       Nothing is lost: the row, the unread state and the badge all keep it. */
    if (callPopupStore.get().calls.length === 0) {
      showDesktopNotification(stored, {
        showPreview: settings.showPreview,
        onActivate: options.onActivate,
      });
    }
    if (decision.sound) chime();
    return;
  }

  if (POPUP_KINDS.has(stored.kind)) popupActions.push(stored);
  else if (!HANDLED_ELSEWHERE.has(stored.kind)) notify.activity(stored, options.onActivate);

  if (decision.sound) chime();
  /* A browser only. In the shell, reaching this line means the shell is NOT
     drawing banners - the card above is the banner, and asking the shell as
     well would be an IPC call it answers by doing nothing. */
  if (decision.desktop && !isDesktopShell()) {
    showDesktopNotification(stored, {
      showPreview: settings.showPreview,
      onActivate: options.onActivate,
    });
  }
}
